/**
 * RichTextEditor: a minimal TipTap rich-text editor.
 *
 * P5 wave 3a (admin content editor). Used by the `paragraph` block edit form
 * (ParagraphForm) to author the `data.html` rich-text payload.
 *
 * Extensions:
 *   - StarterKit: paragraphs, bold, italic, bullet/ordered lists, history,
 *                   and the rest of the baseline schema. Its bundled Link
 *                   extension is disabled (`link: false`) in favor of the
 *                   standalone one below, which carries the paste-XSS guard.
 *   - Link: inline hyperlinks, configured separately so the `isAllowedUri`
 *             hook below is guaranteed to be the only Link extension active.
 *
 * Contract:
 *   - Initializes its document from the `html` prop (the block's existing
 *     `data.html`); an empty/missing `html` starts a blank document.
 *   - Emits HTML: calls `onChange(editor.getHTML())` on every update. The
 *     renderer's ParagraphBlock sanitizes that HTML with DOMPurify on
 *     display, so the editor only needs to emit reasonable markup.
 *
 * The toolbar exposes bold, italic, link (add/remove), and bullet/ordered
 * list. `window.prompt` is used for the link URL: a deliberately minimal
 * affordance for this first editor wave; a richer link popover is future work
 * and is not load-bearing for authoring.
 */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { ButtonGroup, Button } from 'react-bootstrap';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';

import { isAllowedLinkUrl } from '../utils/urlValidator';

export default function RichTextEditor({ html, onChange }) {
    const editor = useEditor({
        // v3 defaults this to false, so the editor stops re-rendering on
        // every transaction; every editor.isActive(...) toolbar variant
        // below would freeze at its initial value without this.
        shouldRerenderOnTransaction: true,
        extensions: [
            // v3's StarterKit bundles its own Link extension; disabled here
            // so the standalone, validated Link below is the only one
            // registered.
            StarterKit.configure({ link: false }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: { rel: 'noopener noreferrer' },
                // Round-5 Wave H finding 4 (stored-XSS-via-paste): the
                // setLink toolbar action below already gates URLs through
                // `isAllowedLinkUrl`, but TipTap's Link extension ALSO
                // accepts hrefs that arrive via paste / autolink without
                // a default scheme check. Pasting `<a href="javascript:
                // alert(1)">x</a>` from the clipboard would otherwise
                // preserve the malicious href on the link mark and
                // serialize it into `editor.getHTML()`, which the
                // ParagraphBlock then stores in `data.html`. The
                // ParagraphBlock renderer DOMPurifies on display, but
                // any future consumer that bypasses DOMPurify (PDF
                // export, email, server-rendered preview, admin diff
                // viewer) would execute the payload. `isAllowedUri` is
                // v3's hook for rejecting disallowed hrefs at link-mark
                // creation time (v2's `validate` option is dropped by
                // this Link extension in favor of it; `validate` is
                // silently inert here, not an alias for this check).
                isAllowedUri: (href) => isAllowedLinkUrl(href),
            }),
        ],
        // Initialize from the existing authored HTML (blank when empty).
        content: html || '',
        onUpdate: ({ editor: ed }) => {
            // An "empty" TipTap document still serializes to "<p></p>";
            // collapse that to an empty string so the block reads as
            // unauthored rather than carrying a stray empty paragraph.
            const next = ed.isEmpty ? '' : ed.getHTML();
            onChange(next);
        },
    });

    // Prompt for a URL and apply (or clear) the link mark on the selection.
    const setLink = useCallback(() => {
        if (!editor) {
            return;
        }
        const previous = editor.getAttributes('link').href || '';

        const url = window.prompt(
            'Link URL (leave blank to remove):',
            previous
        );
        // Cancelled: leave the document untouched.
        if (url === null) {
            return;
        }
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        // Reject `javascript:` / `data:` / `vbscript:` / protocol-relative
        // URLs (round-2 review finding 3: stored XSS via link mark). The
        // renderer's DOMPurify pass already strips these on display, but
        // keeping them out of the document defangs any future renderer
        // (PDF export, email) that bypasses DOMPurify.
        if (!isAllowedLinkUrl(url)) {
            window.alert(
                'That link is not allowed. Use an http(s):// URL, mailto:, or a path beginning with /.'
            );
            return;
        }
        editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .setLink({ href: url })
            .run();
    }, [editor]);

    if (!editor) {
        return null;
    }

    return (
        <div className="content-editor-richtext border rounded">
            <ButtonGroup
                size="sm"
                className="content-editor-richtext__toolbar border-bottom p-1"
            >
                <Button
                    type="button"
                    variant={
                        editor.isActive('bold')
                            ? 'secondary'
                            : 'outline-secondary'
                    }
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    aria-label="Bold"
                >
                    <strong>B</strong>
                </Button>
                <Button
                    type="button"
                    variant={
                        editor.isActive('italic')
                            ? 'secondary'
                            : 'outline-secondary'
                    }
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    aria-label="Italic"
                >
                    <em>I</em>
                </Button>
                <Button
                    type="button"
                    variant={
                        editor.isActive('link')
                            ? 'secondary'
                            : 'outline-secondary'
                    }
                    onClick={setLink}
                    aria-label="Link"
                >
                    🔗
                </Button>
                <Button
                    type="button"
                    variant={
                        editor.isActive('bulletList')
                            ? 'secondary'
                            : 'outline-secondary'
                    }
                    onClick={() =>
                        editor.chain().focus().toggleBulletList().run()
                    }
                    aria-label="Bulleted list"
                >
                    • List
                </Button>
                <Button
                    type="button"
                    variant={
                        editor.isActive('orderedList')
                            ? 'secondary'
                            : 'outline-secondary'
                    }
                    onClick={() =>
                        editor.chain().focus().toggleOrderedList().run()
                    }
                    aria-label="Numbered list"
                >
                    1. List
                </Button>
            </ButtonGroup>
            <EditorContent
                editor={editor}
                className="content-editor-richtext__body p-2"
            />
        </div>
    );
}

RichTextEditor.propTypes = {
    // The existing authored HTML used to initialize the document.
    html: PropTypes.string,
    // Called with the editor's HTML ('' when the document is empty).
    onChange: PropTypes.func.isRequired,
};

RichTextEditor.defaultProps = {
    html: '',
};
